import { addMoney, getTotalPrice as getTotalPriceUtil } from '@shared/lib/money';
import { createSelector } from 'reselect';

import {
	AviaPassengerType,
	BaggageType,
	CheckinInfoParameters,
	CheckinOrderFragment,
	CheckinOrderStatus,
	CheckinSegmentFragment,
	FareFamilyFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageService,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment as MealService,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipService,
	GdsServiceFragment,
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType,
	PriceBreakdownDetailsFragment,
	SelectedInsurance,
	TravellerFieldEnum,
	CheckinPassengerFragment
} from '@websky/graphql';

import { CheckinState } from '../reducers';
import { Passenger } from '../../../../MealPage/types';
import { convertBaggageObject, convertMealObject, PassengerLabels } from '../../../../Checkout/types';
import { getLinkedTravellersMap, getSeatMapUniqueAvailableSegments, getTravellerName } from '../../../../utils';
import { Meal } from '../../../../Meal/types';
import { Money } from '../../../../types';
import { Baggage } from '../../../../BaggageService/types';
import { SelectedServicesState } from '../selectedServices/reducers';
import { PassengerState, PassengerValue } from '../../../../Checkout/store/passengers/reducers';
import { parseIncludedBaggage } from '../../../../BaggagePage/utils';
import { useConfig } from '../../../../context';
import { getIncludedBaggageFromOrderTravellers, getIsAllSeatsSelected } from '../../../../Checkout/utils';
import { getHasRequestedServices, getUserValue } from '../../../../utils';
import { getEXSTServices } from '@shared/lib/seat-map';
import { getMiscServiceGroups as getMiscServiceGroupsFn, buildGdsServicesById } from '@shared/lib/gdsService';
import { getImportantInsuranceCodesByAvailableCodes } from '../../../../InsurancePage/utils';

const getState = (state: CheckinState) => state;

export const getCheckinOrder = createSelector(getState, ({ order }) => order);

export const getAdditionalServices = createSelector(getCheckinOrder, order => order.additionalServices);

export const getTravellers = createSelector(getCheckinOrder, order => order?.travellers ?? []);

export const getTravellersServices = createSelector(
	getTravellers,
	travellers => travellers?.map(item => item.services) ?? []
);

export const getIsContactsInfoEmpty = createSelector(getCheckinOrder, ({ customer }) => customer.contactInfoIsEmpty);

export const getDeprecatedPassengersState = createSelector(getTravellers, travellers => {
	const linkedPassengersIdsMap = getLinkedTravellersMap(travellers);

	return travellers.map(
		(traveller): PassengerState => ({
			id: traveller.id,
			type: traveller.type as AviaPassengerType,
			values: traveller.values as PassengerValue[],
			services: traveller.services?.gdsServices?.services ?? [],
			seats:
				traveller.services?.seats?.map(seat => ({
					id: seat.seat.seatService?.id,
					type: OrderAdditionalServiceGdsServiceServiceType.Seat,
					segmentId: seat.segment.id,
					isConfirmed: seat.isConfirmed,
					row: seat.row.toString(),
					letter: seat.letter,
					price: seat.seat.price,
					number: seat.seat.number,
					rfisc: seat.seat.rfisc
				})) ?? [],
			seatRestrictions: traveller.seatRestrictions,
			infantPassengerId:
				traveller.type === AviaPassengerType.ADT ? linkedPassengersIdsMap.get(traveller.id)?.[0] : undefined,
			linkedTravellerIds: linkedPassengersIdsMap.get(traveller.id),
			supplierTravellerType: traveller.supplierTravellerType
		})
	);
});

export const getTravellersWithVisa = createSelector(getCheckinOrder, ({ travellers }) =>
	travellers.filter(traveller => traveller.visaRequired && traveller.visaValues.length)
);

export const getPassengerIdsWithLinkedPassengers = createSelector(getCheckinOrder, order => {
	const passengerIdsWithLinkedPassengersMap = new Map<string, CheckinPassengerFragment[]>();

	order.travellers.forEach(traveller => {
		if (traveller.linkedTraveller) {
			const linkedTravellerId = traveller.linkedTraveller.id;

			passengerIdsWithLinkedPassengersMap.set(linkedTravellerId, [
				...(passengerIdsWithLinkedPassengersMap.get(linkedTravellerId) ?? []),
				traveller
			]);
		}
	});

	passengerIdsWithLinkedPassengersMap.forEach((travellers, travellerId) => {
		travellers.forEach(linkedTraveller => {
			const mainTraveller = order.travellers.find(traveller => traveller.id === travellerId);

			if (mainTraveller) {
				const linkedTravellers = [
					...travellers.filter(traveller => traveller.id !== linkedTraveller.id),
					mainTraveller
				];

				passengerIdsWithLinkedPassengersMap.set(linkedTraveller.id, linkedTravellers);
			}
		});
	});

	return passengerIdsWithLinkedPassengersMap;
});

export const getPassengersNames = createSelector(getTravellers, (travellers): Passenger[] =>
	travellers.map(traveller => ({
		id: traveller.id,
		name: getTravellerName(traveller)
	}))
);

export const getInsurance = createSelector(getCheckinOrder, order => order.additionalServices.insurances);

export const getMinInsurancePrice = createSelector(
	getInsurance,
	(insurances): Money => ({
		amount: Math.min(...insurances.insurancePrograms.map(program => program.totalPrice.amount)),
		currency: insurances.insurancePrograms[0]?.totalPrice?.currency
	})
);

export const getTotalInsurancePrice = createSelector(getInsurance, insurances => {
	if (insurances.selectedInsurances?.length) {
		return getTotalPriceUtil(insurances.selectedInsurances, insurance => insurance.passengerPrice);
	}

	return null;
});

export const getConfirmedInsurances = createSelector(
	getInsurance,
	(insurance): SelectedInsurance[] =>
		(insurance.selectedInsurances as SelectedInsurance[]).filter(
			insurance => !!insurance.policyUrl
		) as SelectedInsurance[]
);

export const getImportantInsuranceCodes = (availableInsuranceCodes: string[] = []) =>
	createSelector(getInsurance, insurance =>
		getImportantInsuranceCodesByAvailableCodes(insurance, availableInsuranceCodes)
	);

export const getTotalPrice = createSelector(getCheckinOrder, ({ priceToPay }): Money => ({ ...priceToPay.total }));

export const getCheckinOrderId = createSelector(getCheckinOrder, ({ id }): string => id);

export const getCheckinSegments = createSelector([getCheckinOrder], order => order.segments);

export const getCheckinNotStarted = createSelector(getState, ({ order }) => order.status === CheckinOrderStatus.New);

export const getCheckinIsBlockedForPayment = createSelector(
	getCheckinOrder,
	({ isBlockedForPayment }) => isBlockedForPayment
);

export const getCheckinUnavailableSegments = createSelector(getCheckinSegments, segments =>
	segments.filter(segment => !segment.checkinInfo?.checkinAvailable)
);

export const getHasUnavailableCheckinSegments = createSelector(
	getCheckinUnavailableSegments,
	unavailableSegments => unavailableSegments.length > 0
);

export const getCheckinHasStarted = createSelector(
	getState,
	({ order }) => order?.status === CheckinOrderStatus.Started
);

export const getPriceToPay = createSelector(
	getCheckinOrder,
	({ priceToPay }): CheckinOrderFragment['priceToPay'] => priceToPay
);

export const getTotalPriceToPay = createSelector(
	getPriceToPay,
	({ total }): Money => total ?? { amount: 0, currency: total.currency }
);

export const getCurrentCurrencyFromCheckinOrder = createSelector(getTotalPrice, totalPrice => totalPrice?.currency);

export const getNeedToFillContactsInfo = createSelector(
	getTotalPriceToPay,
	getIsContactsInfoEmpty,
	(totalPrice, contactsInfoIsEmpty) => {
		console.log(totalPrice, contactsInfoIsEmpty);

		return totalPrice?.amount > 0 && contactsInfoIsEmpty;
	}
);

export const getCheckinInfoParams = createSelector(
	getCheckinOrder,
	({ travellers }): CheckinInfoParameters => ({
		secret: getUserValue(travellers[0], TravellerFieldEnum.LastName),
		id: travellers[0]?.tickets[0]?.number
	})
);

export const getCheckinHasCompleted = createSelector(
	getState,
	({ order }) => order.status === CheckinOrderStatus.Confirmed
);

export const getCheckinMessages = createSelector(getCheckinOrder, order => order.messages);

export const isExpired = createSelector(getState, ({ order }) => order.status === CheckinOrderStatus.Expired);

export const getAllServices = createSelector(
	getAdditionalServices,
	additionalServices => additionalServices?.gdsServices?.services ?? []
);

export const getGdsServicesById = createSelector(getAllServices, buildGdsServicesById);

export const makeGetGdsServicesByType = <T extends GdsServiceFragment>(
	type: OrderAdditionalServiceGdsServiceServiceType
) =>
	createSelector([getAllServices], checkinGdsServices => {
		return (checkinGdsServices.filter(service => service.type === type) as unknown) as T[];
	});

export const getExtraSeatServices = createSelector(getAllServices, getEXSTServices);

export const getUpgradeServices = createSelector(getAllServices, services =>
	services.filter(
		service =>
			service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
			(service as VipService).subType === GdsServiceVipSubType.Upgrade
	)
);

export const getBaggageServices = createSelector(
	getAllServices,
	(services): BaggageService[] =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
		) as BaggageService[]
);

export const getMealServices = createSelector(
	getAllServices,
	(services): MealService[] =>
		services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Meal) as MealService[]
);

export const getVipServices = createSelector(getAllServices, services => {
	return services.filter(service => {
		const vipService = service as VipService;

		return (
			vipService.type === OrderAdditionalServiceGdsServiceServiceType.Vip ||
			vipService.type === OrderAdditionalServiceGdsServiceServiceType.BusinessLounge
		);
	});
});

export const getMeals = createSelector(getMealServices, (meals): Meal[] => meals.map(convertMealObject));
export const getBaggage = createSelector(getBaggageServices, (baggage): Baggage[] => baggage.map(convertBaggageObject));

export const getSeatMap = createSelector(getCheckinOrder, ({ seatMap }) => seatMap);

export const getSeatMapAvailableSegments = createSelector(getSeatMap, seatMap =>
	getSeatMapUniqueAvailableSegments(seatMap.segments)
);

export const getIsSeatMapUnavailable = createSelector(getSeatMap, getTravellers, (seatMap, travellers) => {
	const hasSegmentsWithZeroAvailableSeats = seatMap?.segments.some(segment => !segment.availableSeatsCount);
	const allTravellersHaveNotSeats = travellers.every(traveller => !traveller.services?.seats?.length);

	return hasSegmentsWithZeroAvailableSeats && allTravellersHaveNotSeats;
});

export const getInsurances = createSelector(getCheckinOrder, ({ additionalServices }) => additionalServices.insurances);

export const getTransfer = createSelector(getCheckinOrder, ({ additionalServices }) => additionalServices.transfer);

export const hasBaggageServices = createSelector(getBaggageServices, baggage => !!baggage.length);
export const hasUpgradeServices = createSelector(getUpgradeServices, upgradeServices =>
	upgradeServices.some(service => service.canBeAdded)
);

export const hasMealServices = createSelector(getMealServices, meal => !!meal.length);

export const hasSeatMap = createSelector(
	getSeatMap,
	getAdditionalServices,
	(seatMap, additionalServices) =>
		!!seatMap.segments?.some(segment => segment.isAnySeatExistsAndFree) ||
		!!additionalServices.gdsServices?.services?.length
);

export const getHasAvailableSeats = createSelector(getSeatMap, seatMap =>
	seatMap?.segments?.some(segment => segment?.availableSeatsCount)
);

export const getMiscServiceGroups = createSelector(getAllServices, getMiscServiceGroupsFn);

export const hasInsuranceSeparateStep = createSelector(getInsurances, insurances => {
	return !!insurances?.insurancePrograms?.length && !!insurances?.separateStep;
});

export const hasExtrasStepServices = createSelector(
	getVipServices,
	getInsurances,
	hasInsuranceSeparateStep,
	getTransfer,
	getMiscServiceGroups,
	(vipServices, insurances, insuranceAsSeparateStep, transfer, miscServicesGroups) => {
		const hasVipServices =
			!!vipServices.length &&
			vipServices.some(service => (service as VipService).subType !== GdsServiceVipSubType.Subscription);
		let hasInsurances = !!insurances?.insurancePrograms?.length;
		const hasTransfer = !!transfer?.aeroexpress?.trips?.length;
		const hasMiscServices = !!miscServicesGroups.length;

		if (insuranceAsSeparateStep) {
			hasInsurances = false;
		}

		return hasVipServices || hasInsurances || hasTransfer || hasMiscServices;
	}
);

export const getSegments = createSelector(getCheckinOrder, order => order.segments);

export const getFareFamiliesMap = createSelector(getSegments, segmentsFromOrder => {
	const fareFamiliesMap = new Map<string, { fareFamily: FareFamilyFragment; segmentIds: string[] }>();

	segmentsFromOrder.forEach(({ id, fareFamilies }) => {
		const fareFamily = fareFamilies.length > 0 ? fareFamilies[0].fareFamily : null;

		if (fareFamily) {
			if (!fareFamiliesMap.has(fareFamily.id)) {
				fareFamiliesMap.set(fareFamily.id, {
					fareFamily,
					segmentIds: []
				});
			}

			fareFamiliesMap.get(fareFamily.id).segmentIds.push(id);
		}
	});

	return fareFamiliesMap;
});

export const getCheckinIncludedBaggage = createSelector(
	getFareFamiliesMap,
	getTravellers,
	useConfig,
	(fareFamiliesMap, travellers, config) => {
		if (config?.global?.companyInfo?.iataCode === 'N4') {
			// filter out all included baggage for INF and INS
			const travellersWithoutInfants = travellers.filter(
				traveller => traveller.type !== AviaPassengerType.INF && traveller.type !== AviaPassengerType.INS
			);

			return fareFamiliesMap
				? parseIncludedBaggage(
						fareFamiliesMap,
						travellersWithoutInfants.map(({ id }) => id)
				  )
				: [];
		}

		return parseIncludedBaggage(
			fareFamiliesMap,
			travellers.map(({ id }) => id)
		);
	}
);

export const getCheckinBrandIncludedBaggage = createSelector(getTravellers, travellers => {
	return getIncludedBaggageFromOrderTravellers(
		travellers.map(traveller => ({
			...traveller,
			needOthsSsr: null,
			fares: []
		}))
	);
});

export const getSegmentsForServices = createSelector(getSegments, (segments): CheckinSegmentFragment[] => segments);

export const getPriceBreakdown = createSelector(
	getCheckinOrder,
	({ priceBreakdown }): PriceBreakdownDetailsFragment => priceBreakdown
);

export const getMealsPrice = createSelector(
	getCheckinOrder,
	getCurrentCurrencyFromCheckinOrder,
	({ priceToPay }, currentCurrency): Money =>
		priceToPay.meals ? { ...priceToPay.meals, currency: currentCurrency } : { amount: 0, currency: currentCurrency }
);

export const getBaggagePrice = createSelector(
	getCheckinOrder,
	getCurrentCurrencyFromCheckinOrder,
	({ priceToPay }, currentCurrency): Money =>
		priceToPay.baggage
			? { ...priceToPay.baggage, currency: currentCurrency }
			: { amount: 0, currency: currentCurrency }
);

export const getSeatsPrice = createSelector(
	getCheckinOrder,
	getCurrentCurrencyFromCheckinOrder,
	({ priceToPay }, currentCurrency): Money =>
		priceToPay.seats ? { ...priceToPay.seats, currency: currentCurrency } : { amount: 0, currency: currentCurrency }
);

export const getInsurancePrice = createSelector(
	getCheckinOrder,
	getCurrentCurrencyFromCheckinOrder,
	({ priceToPay }, currentCurrency) =>
		priceToPay.insurance
			? { ...priceToPay.insurance, currency: currentCurrency }
			: { amount: 0, currency: currentCurrency }
);

export const getAeroexpressPrice = createSelector(
	getCheckinOrder,
	getCurrentCurrencyFromCheckinOrder,
	({ priceToPay }, currentCurrency) =>
		priceToPay.aeroexpress
			? { ...priceToPay.aeroexpress, currency: currentCurrency }
			: { amount: 0, currency: currentCurrency }
);

export const getHasSelectedAeroexpress = createSelector(
	getCheckinOrder,
	order => !!order?.additionalServices?.transfer?.aeroexpress?.selectedTrips?.length
);
export const getHasConfirmedAeroexpress = createSelector(
	getCheckinOrder,
	order =>
		!!order?.additionalServices?.transfer?.aeroexpress?.selectedTrips?.some(trip =>
			trip?.tickets?.some(ticket => ticket?.imageUrl)
		)
);

export const getExtrasPrice = createSelector(
	getAeroexpressPrice,
	getInsurancePrice,
	getCurrentCurrencyFromCheckinOrder,
	(aeroexpress, insurance, currentCurrency) => ({
		...addMoney(aeroexpress, insurance),
		currency: currentCurrency
	})
);

export const getIsAllTravellersHavePreseating = createSelector(
	getCheckinOrder,
	order => order.allTravellersHavePreseating
);

export const getIsCheckinCompletedOrPreseating = createSelector(
	getCheckinHasCompleted,
	getIsAllTravellersHavePreseating,
	(isCheckinCompleted, isAllTravellersHavePreseating) => isCheckinCompleted || isAllTravellersHavePreseating
);

export const getConfirmedServices = createSelector(
	getTravellers,
	(travellers): SelectedServicesState => {
		const result: SelectedServicesState = {};

		travellers.forEach(traveller => {
			const services = traveller?.services?.gdsServices?.services ?? [];

			services.forEach(({ confirmedCount, serviceId, segmentIds }) => {
				if (confirmedCount > 0) {
					if (!result[traveller.id]) {
						result[traveller.id] = {};
					}

					if (!result[traveller.id][serviceId]) {
						result[traveller.id][serviceId] = {};
					}

					segmentIds.forEach(segmentId => {
						result[traveller.id][serviceId][segmentId] = confirmedCount;
					});
				}
			});
		});

		return result;
	}
);

export const getAllRequiredSeatsAreSelected = createSelector(
	getDeprecatedPassengersState,
	getSeatMapAvailableSegments,
	getIsAllSeatsSelected
);

export const getUpgradeBaggage = createSelector(getBaggage, getTravellers, (baggageItems, travellers) => {
	const travellersByIdMap = new Map(
		travellers.map(traveller => {
			if (!traveller.services.gdsServices?.services?.length) {
				return [traveller.id, null];
			}

			const travellerGdsServicesById = new Map(
				traveller.services.gdsServices.services.map(service => [
					service.serviceId,
					service.count > service.confirmedCount
				])
			);

			return [traveller.id, travellerGdsServicesById];
		})
	);

	const isCanBeAddedOrRemoved = (item: Baggage) => {
		const canBeRemoved = item.allowedPassengers.some(passengerId =>
			travellersByIdMap.get(passengerId)?.get(item.id)
		);

		return item.canBeAdded || canBeRemoved;
	};

	return baggageItems.filter(item => {
		return item.type === BaggageType.HandLuggageExcess && isCanBeAddedOrRemoved(item);
	});
});

export const getAdditionalBaggage = createSelector(getBaggage, items => {
	return items.filter(item => item.type === BaggageType.CheckedBaggage);
});

export const getSpecialEquipment = createSelector(getBaggage, items => {
	return items.filter(item =>
		[
			BaggageType.SportingEquipment,
			BaggageType.PetInCabin,
			BaggageType.PetInHold,
			BaggageType.MusicalInstrument
		].includes(item.type)
	);
});

export const getOverweightBaggage = createSelector(getBaggage, items =>
	items.filter(item => item.type === BaggageType.BaggageExcess)
);

export const getUpgradableBaggage = createSelector(
	[getUpgradeBaggage, getAdditionalBaggage, getSpecialEquipment, getOverweightBaggage],
	(...baggage) => baggage.reduce((services, items) => [...services, ...items], [])
);

export const getIsTravellerDataFilled = createSelector(getCheckinOrder, order => order.isTravellersDataFilled);

export const getCheckinPassengerLabels = createSelector(
	getPassengersNames,
	(passengers): PassengerLabels => {
		const result: PassengerLabels = {};

		passengers.forEach(passenger => {
			result[passenger.id] = passenger.name;
		});

		return result;
	}
);

export const getHasRequestedOrderServices = createSelector(
	getCheckinHasStarted,
	getTravellers,
	(checkinStarted, travellers) => {
		return checkinStarted && getHasRequestedServices(travellers);
	}
);

export const getIsSkipSeatSelectionAllowed = createSelector(getCheckinOrder, order => order.isSkipSeatSelectionAllowed);
